import get from "lodash/get";
import find from "lodash/find";
import findIndex from "lodash/findIndex";

import { isNullOrEmpty, lookupCode } from '@/services/util.service';

const filterVisibleOptions = (options, selectedAttributes) => {
  return options.filter(option => {
    const usageAllowed = !option.allowedUsage || option.allowedUsage.find(_allowed => {
      return _allowed === 'OWA' || _allowed === 'ALL'
    });
    if (!usageAllowed) return false;

    if (option.valueDependencies && option.valueDependencies.dependencies) {
      const { dependencies, type } = option.valueDependencies;

      const checkDependency = (dependency) => {
        const { attributeCode, valueCode } = dependency;
        const selectedAttribute = selectedAttributes.find(attr => attr.code === attributeCode);
        if (!selectedAttribute) return false;
        return selectedAttribute.attrValueList.some(value => value.code === valueCode);
      };

      if (type === 'AND') {
        return dependencies.every(checkDependency);
      } else if (type === 'OR') {
        return dependencies.some(checkDependency);
      } else {
        // Unexpected type value; do not show this option
        return false;
      }
    }

    return true;
  });
};

const autoFillOptions = (options) => {
  for (let option of options) {
    const hasMultiValue = !!option.values.find(value => {
      const { maxCardinality = -1 } = value || {}

      return maxCardinality > 1 || maxCardinality === -1
    })

    // UI gets confusing if we auto select with multi options so skip this feature in those cases
    if (hasMultiValue)
      continue

    // If the min cardinality requires all options be selected, auto select them
    if (!isNullOrEmpty(option.values) && option.minCardinality >= option.values.length) {
      option.values.forEach((value) => {
        value.selected = true;
      });
    }
  }
}
const autoFillRelations = (relations, getters) => {
  if (!isNullOrEmpty(relations)) {
    relations.forEach((relation) => {
      relation.selectedItem = []

      if (!isNullOrEmpty(relation.menuItemCodes) && relation.menuItemCodes.length == 1 && relation.minCardinality > 0) {
        relation.menuItemCodes.forEach((code) => {
          const menuItem = lookupCode(code);
          if (!menuItem.outOfStock && !(menuItem.attributes && menuItem.attributes.Configurable)) {
            relation.selectedItem.push({
              ...menuItem,
              qty: relation.minCardinality
            });
          }
        });
      }
    });
  }
}

export default {
  state: {
    menuItem: {},
    isCartItem: false,
    cartItemIndex: null,
    isOption: false,

    configCoupon: null,
    activeCoupon: null,
    activeCouponStoreId: null, // store the active coupon was applied in (DEV-11388)
    compositeItemQueue: [],
    lastConfiguredRelationIndex: null
  },

  getters: {
    getIsOption(state) {
      return state.isOption
    },
    getCartItemIndex(state) {
      return state.cartItemIndex
    },
    getWorkingMenuItem(state) {
      return state.menuItem
    },
    getIsCartItem(state) {
      return state.isCartItem
    },
    getOptionValidityMap(state) {
      const meetsRequirements = (qty, minCardinality, maxCardinality) => {
        return qty >= minCardinality && (qty <= maxCardinality || maxCardinality === -1)
      }

      const options = get(state, 'menuItem.options', [])

      const attributes = get(state, 'menuItem.itemAttributeList', []) || []
      const optionAttributeMap = (attributes).reduce((prev, attribute) => {
        const { attrValueList, code: optCode } = attribute
        return {
          ...prev,
          [ optCode ]: { attrValueList }
        }
      }, {})

      return options.reduce((prev, option) => {
        let { minCardinality, maxCardinality, code } = option
        if (Array.isArray(option.cardinalityDependencies) && option.cardinalityDependencies.length > 0) {
          for (const dependency of option.cardinalityDependencies) {
            if (!dependency || !dependency.attributeCode || !dependency.attributeValueCode) {
              continue
            }

            const { attributeCode, attributeValueCode, minCardinality: depMin, maxCardinality: depMax } = dependency
            const selectedAttribute = attributes.find(attr => attr.code === attributeCode)

            if (selectedAttribute && Array.isArray(selectedAttribute.attrValueList)) {
              const selectedValue = selectedAttribute.attrValueList.find(value => value.code === attributeValueCode)
              if (selectedValue) {
                if (typeof depMin === 'number' && typeof depMax === 'number') {
                  minCardinality = depMin
                  maxCardinality = depMax
                  break
                }
              }
            }
          }
        }

        const attribute = optionAttributeMap[ code ]
        const { attrValueList = [] } = attribute || {}

        let finalQty = 0
        let valuesMap = {}

        if (!Array.isArray(option.values)) {
          return {
            ...prev,
            [code]: false
          }
        }

        option.values.forEach(_value => {
          const _matched = attrValueList.find(_atr => _atr.code == _value.code)
          _value = {
            ..._value,
            // Need to iterate the full list, but only attrValueList has qty
            qty: get(_matched, 'qty', 0)
          }

          // Checkbox and radio buttons don't make use of qty (since it's selected or not / 0 or 1)
          // So if the item has no qty but is in the attrValueList, it is selected and should have qty 1 for counting purposes
          if (!_value.qty && !!_matched)
            _value.qty = 1

          // The sum of quantities must meet the modifiers requirements
          // As well as each value must meet its own requirements 
          finalQty += _value.qty // === undefined ? 1 : _value.qty
          valuesMap[_value.code] = meetsRequirements(_value.qty, _value.minCardinality, _value.maxCardinality)
        })

        return {
          ...prev,
          // Only true when the modifer and all it's childrens cardinality requirements are met
          [code]: meetsRequirements(finalQty, minCardinality, maxCardinality) && Object.values(valuesMap).reduce((prev, curr) => prev && curr, true)
        }
      }, {})
    },
    isWorkingMenuItemValid(state, getters) {
      const allOptions = get(state, 'menuItem.options', [])
      const attributes = get(state, 'menuItem.itemAttributeList', [])
      const visibleOptions = filterVisibleOptions(allOptions, attributes)

      if (visibleOptions.length <= 0) {
        return true
      }

      const validityResultMap = getters.getOptionValidityMap

      const visibleOptionCodes = visibleOptions.map(opt => opt.code)
      const visibleValidityResults = Object.keys(validityResultMap)
        .filter(code => visibleOptionCodes.includes(code))
        .map(code => validityResultMap[code])

      if (visibleValidityResults.length === 0) {
        return true
      }

      return visibleValidityResults.reduce((prev, curr) => prev && curr, true)
    },

    getConfigCoupon: (state) => state.configCoupon,
    getActiveCoupon(state, getters, rootState, rootGetters) {
      if (!state.activeCoupon) return []
      // Saved across reloads (DEV-11388) -- only ever applies in the store it was applied in.
      if (state.activeCouponStoreId && state.activeCouponStoreId !== rootGetters.getUniqueStoreId) return []
      return [state.activeCoupon]
    },
    getCompositeQueue: (state) => state.compositeItemQueue,
    getLastConfiguredRelationIndex: (state) => state.lastConfiguredRelationIndex
  },

  mutations: {
    SET_IS_OPTION(state, isOption){
      return state.isOption = isOption
    },
    SET_CART_ITEM_INDEX(state, index) {
      return state.cartItemIndex = index
    },
    SET_WORKING_MENU_ITEM(state, newItem) {
      if (newItem === null) {
        state.menuItem = null
        return
      }

      // only called for items with configs
      // pull from different locations based on if from cart or clicked from store
      let imgPath = get(newItem, 'images.MAIN[0].url', null)
      if (imgPath) {
        imgPath = `${process.env.VUE_APP_API_IMAGE}${imgPath}`
      } else {
        imgPath = get(newItem, 'image', undefined)
      }

      function checkSelectedItems(obj) {
        for (let relation of obj.relations) {
          if (relation.selectedItem && relation.selectedItem.length) {
            return true;
          }
        }
        return false;
      }

      const isBeingUpdated = state.menuItem && state.menuItem.code == newItem.code
      let newItemRelationsExist = false;
      let menuItemRelationsExist = false;

      if (newItem && newItem.relations && newItem.relations.length) {
        newItemRelationsExist = true;
      }

      if (
        state.menuItem &&
        state.menuItem.relations &&
        state.menuItem.relations.length
      ) {
        menuItemRelationsExist = true;
      }

      let comboEditing = false;

      if (newItemRelationsExist && menuItemRelationsExist) {
        comboEditing =
          checkSelectedItems(newItem) || checkSelectedItems(state.menuItem);
      } else if (newItemRelationsExist) {
        comboEditing = checkSelectedItems(newItem);
      } else if (menuItemRelationsExist) {
        comboEditing = checkSelectedItems(state.menuItem);
      }


      state.menuItem = {
        ...newItem,
        code: get(newItem, 'code'),
        name: get(newItem, 'name', ''),
        description: get(newItem, 'description', ''),
        specialRequest: get(newItem, 'specialRequest', ''),
        options: get(newItem, 'attributes.Configurable', undefined) || get(newItem, 'options', []),
        itemAttributeList: get(newItem, 'itemAttributeList', []),
        relations: get(newItem, 'relations', []),
        price: get(newItem, 'charges[0].price') || get(newItem, 'price') || 0,
        qty: get(newItem, 'qty', 1),
        image: imgPath,
        raw: newItem,
        tags: get(newItem, 'tags', []) || [],
        errors: get(newItem, 'errors', false),
        childItemList: get(newItem, 'childItemList', [])
      }

      // Only auto fill the item the first time,
      // We reuse this function to store changes but don't need to repopulate defaults again
      if (!state.isCartItem && !isBeingUpdated && !comboEditing) {
        autoFillOptions(state.menuItem.options)
        autoFillRelations(state.menuItem.relations, this.$store.getters)
      }
    },
    SET_IS_CART_ITEM(state, isCartItem) {
      state.isCartItem = isCartItem
    },
    SET_ATTRIBUTE_QUANTITY(state, { attribute, qty, code }) {
      const attributeCode = get(attribute, 'code')
      const optionList = [ ...state.menuItem.itemAttributeList ]
      const foundOptionIndex = findIndex(optionList, o => o.code === code)
      const foundOption = foundOptionIndex !== -1

      const fullOption = find(state.menuItem.attributes.Configurable, o => o.code === code)

      // If the option found, it may be the first time we are modifying the attribute
      let foundAttribute = false
      let attributeList = []
      let foundAttributeIndex = -1
      if (foundOption) {
        const option = { ...optionList[ foundOptionIndex ] }
        attributeList = [ ...get(option, 'attrValueList', []) ]

        foundAttributeIndex = findIndex(attributeList, attr => {
          return attr.code === attributeCode
        })
        foundAttribute = foundAttributeIndex !== -1
      }

      const menuItemCopy = { ...state.menuItem }
      if (!foundAttribute && qty > 0) {
        attributeList.push({ ...attribute, qty })
        // If there is no attribute, but the quantity is positive, add
        if (foundOption) {
          menuItemCopy.itemAttributeList[ foundOptionIndex ].attrValueList = attributeList
        } else {
          menuItemCopy.itemAttributeList.push({
            code,
            multiSelect: fullOption.multiSelect,
            priceInfluencer: fullOption.priceInfluencer,
            priceable: fullOption.priceable,
            pizzaTopping: fullOption.pizzaTopping,
            numFree: fullOption.numFree,
            attrValueList: attributeList
          })
        }
      } else if ((foundAttribute) && qty > 0) {
        // If the attribute exists, and the quantity is positive, set value
        attributeList[ foundAttributeIndex ] = { ...attribute, qty }
        menuItemCopy.itemAttributeList[ foundOptionIndex ].attrValueList = attributeList
      } else if ((foundAttribute) && qty === 0) {
        // If the attribute exists, but there is no quantity, remove
        attributeList.splice(foundAttributeIndex, 1)
        menuItemCopy.itemAttributeList[ foundOptionIndex ].attrValueList = attributeList
      }

      state.menuItem = { ...menuItemCopy }
    },

    SET_CONFIG_COUPON(state, payload) {
      state.configCoupon = payload
    },
    SET_ACTIVE_COUPON(state, payload) {
      state.activeCoupon = payload
    },
    SET_ACTIVE_COUPON_STORE_ID(state, storeId) {
      state.activeCouponStoreId = storeId
    },
    SET_COMPOSITE_QUEUE(state, payload) {
      state.compositeItemQueue = payload
    },
    SET_LAST_CONFIGURED_RELATION_INDEX(state, index) {
      state.lastConfiguredRelationIndex = index
    }
  },

  actions: {
    setIsOption({ commit }, isOption) {
      commit('SET_IS_OPTION', isOption)
    },
    setWorkingMenuItem({ commit, getters, rootGetters }, newItem) {
      //Ensuring that item qty is not 0
      if (newItem && (!newItem.qty || newItem.qty <= 0)) {
        newItem.qty = 1;
      }
      commit('SET_WORKING_MENU_ITEM', newItem)

      if (newItem) {
        try {
          const enName = (newItem.translations && newItem.translations.find(t => t.lang === 'en'))
            ? newItem.translations.find(t => t.lang === 'en').value
            : (newItem.name || '');
          const itemPrice = Number(newItem.price || (Array.isArray(newItem.charges) && newItem.charges[0] && newItem.charges[0].price) || 0);
          const storeId = String((rootGetters.getStore && rootGetters.getStore.storeId) || '')
          const userId = rootGetters.getUserID ? String(rootGetters.getUserID) : undefined
          const viewItemPayload = {
            event: 'view_item',
            ecommerce: {
              items: [{ item_id: String(newItem.code || ''), item_name: enName, price: itemPrice }],
              store_id: storeId,
              ...(userId && { user_id: userId }),
            },
          };
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(viewItemPayload);
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'view_item', viewItemPayload.ecommerce);
          }
        } catch (e) { console.warn('view_item tracking error', e) }
      }
    },
    setIsCartItem({ commit }, isCartItem) {
      commit('SET_IS_CART_ITEM', isCartItem)
    },
    setCartItemIndex({ commit }, index) {
      commit('SET_CART_ITEM_INDEX', index)
    },
    setAttributeQuantity({ commit }, { attribute, qty, code }) {
      commit('SET_ATTRIBUTE_QUANTITY', { attribute, qty, code })
    }
  }
}
